import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Clock, RotateCcw, Save, Tag, Timer, Zap } from "lucide-react";
import { ApiError } from "@/lib/api";
import {
  clearCategorySlaTarget,
  clearTeamSlaTarget,
  describeMinutes,
  getTeamSla,
  saveCategorySlaTarget,
  saveTeamSlaTarget,
  type SlaTarget,
  type SlaTier,
  type TeamSla,
} from "@/lib/tickets";
import { cn } from "@/lib/utils";
import { ErrorNote, LevelChip, PanelSkeleton } from "./shared";
import { TeamHoursPanel } from "./TeamHoursPanel";
import { TeamHolidaysPanel } from "./TeamHolidaysPanel";
import { TeamOnCallPanel } from "./TeamOnCallPanel";

/**
 * What a team, and each of its categories, promises.
 *
 * ⚠️ THE LADDER IS THE WHOLE IDEA. A figure here comes from the most specific rung that has one
 * — the category's own row, then the team's, then the organisation's default — and the screen
 * says which, because an inherited value shown plainly is a value somebody will try to correct
 * in the wrong place. `tier` arrives from the server; nothing here recomputes it.
 *
 * ⚠️ Editing changes what FUTURE requests promise. Deadlines already set were computed when
 * their request was raised and are left alone, or shortening a target on a Friday would breach
 * every open request retroactively, including ones answered on time. The panel says so.
 *
 * ⚠️ THE CLOCK IS THE POINT, more than the minutes. Every target used to run on business hours
 * because the clock was a property of the priority alone — so a Normal-priority medication
 * issue waited for Monday morning exactly as a furniture request did. A clinical category can
 * now run continuously without moving anything else.
 */
export function TeamSlaPanel({ teamId }: { teamId: string }) {
  const [sla, setSla] = useState<TeamSla | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Which row is open for editing: the team's own, or one of its categories'. One value, so
  // opening a second closes the first — two half-finished edits on screen is how the wrong one
  // gets saved.
  const [editing, setEditing] = useState<{ categoryId: string | null; priority: string } | null>(
    null,
  );

  const load = async () => {
    try {
      setSla(await getTeamSla(teamId));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load the response targets.");
      setSla(null);
    }
  };

  useEffect(() => {
    setEditing(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  if (error) return <ErrorNote message={error} />;
  if (sla === null) return <PanelSkeleton />;

  return (
    <div className="space-y-6">
      <p className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          Changes apply to requests raised from now on. Deadlines already set are left alone —
          otherwise shortening a target would mark requests breached that were answered on time. The
          working week and the organisation-wide defaults live in Settings.
        </span>
      </p>

      {/* ⚠️ Above the targets, deliberately. A target is a number of MINUTES and the hours are
          what those minutes are spent against — reading "reply within 4 hr" without knowing
          whether the desk is open at 03:00 is reading half the commitment. */}
      <TeamHoursPanel teamId={teamId} hours={sla.hours} onChanged={load} />

      {/* Beside the hours, because they answer the same question — when does the clock
          advance — and separating them would put half the answer on another screen. */}
      <TeamHolidaysPanel teamId={teamId} holidays={sla.holidays} onChanged={load} />

      {/* Beside both — "when is the clock running" and "who do you call when it isn't" are one
          question from two sides. */}
      <TeamOnCallPanel teamId={teamId} onCallPhone={sla.onCallPhone} onChanged={load} />

      <TargetTable
        heading={`${sla.teamName} — every category, unless one says otherwise`}
        targets={sla.targets}
        editing={editing?.categoryId === null ? editing.priority : null}
        onEdit={(priority) => setEditing({ categoryId: null, priority })}
        onCancel={() => setEditing(null)}
        onSave={async (priority, body) => {
          await saveTeamSlaTarget(teamId, priority, body);
          setEditing(null);
          await load();
        }}
        onClear={async (priority) => {
          await clearTeamSlaTarget(teamId, priority);
          setEditing(null);
          await load();
        }}
      />

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Categories
        </h3>

        {sla.categories.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            This team has no categories yet, so there is nothing to set a different target on.
          </p>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">
            A category inherits its team's targets until you set its own. Only the ones that
            genuinely differ need an override — a password reset and an equipment request are not
            the same promise.
          </p>
        )}

        <div className="mt-3 space-y-3">
          {sla.categories.map((c) => (
            <div key={c.categoryId} className="rounded-lg border border-border">
              <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
                <Tag className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate text-sm font-medium",
                    !c.isActive && "text-muted-foreground line-through",
                  )}
                >
                  {c.name}
                </span>
                <LevelChip level={c.sensitivity} />
                <OverrideCount targets={c.targets} />
              </div>

              <div className="p-3">
                <TargetTable
                  targets={c.targets}
                  editing={editing?.categoryId === c.categoryId ? editing.priority : null}
                  onEdit={(priority) => setEditing({ categoryId: c.categoryId, priority })}
                  onCancel={() => setEditing(null)}
                  onSave={async (priority, body) => {
                    await saveCategorySlaTarget(c.categoryId, priority, body);
                    setEditing(null);
                    await load();
                  }}
                  onClear={async (priority) => {
                    await clearCategorySlaTarget(c.categoryId, priority);
                    setEditing(null);
                    await load();
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** How many of a category's five priorities it actually decides for itself. */
function OverrideCount({ targets }: { targets: SlaTarget[] }) {
  const overrides = targets.filter((t) => t.tier === "Category").length;

  if (overrides === 0) {
    return <span className="shrink-0 text-[11px] text-muted-foreground">inherits everything</span>;
  }

  return (
    <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[11px] text-secondary-foreground">
      {overrides} of {targets.length} set here
    </span>
  );
}

function TargetTable({
  heading,
  targets,
  editing,
  onEdit,
  onCancel,
  onSave,
  onClear,
}: {
  heading?: string;
  targets: SlaTarget[];
  editing: string | null;
  onEdit: (priority: string) => void;
  onCancel: () => void;
  onSave: (
    priority: string,
    body: { firstResponseMinutes: number; resolutionMinutes: number; clock: SlaTarget["clock"] },
  ) => Promise<void>;
  onClear: (priority: string) => Promise<void>;
}) {
  return (
    <div>
      {heading && (
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {heading}
        </h3>
      )}

      <ul className={cn("divide-y divide-border", heading && "mt-2")}>
        {targets.map((t) => (
          <li key={t.priority} className="py-2">
            {editing === t.priority ? (
              <TargetEditor
                target={t}
                onCancel={onCancel}
                onSave={(body) => onSave(t.priority, body)}
                onClear={() => onClear(t.priority)}
              />
            ) : (
              <button
                type="button"
                onClick={() => onEdit(t.priority)}
                className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 rounded px-1 text-left transition hover:bg-secondary/50"
              >
                <span className="w-16 shrink-0 text-sm font-medium">{t.priority}</span>

                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Timer className="h-3.5 w-3.5" aria-hidden />
                  reply {describeMinutes(t.firstResponseMinutes)}
                </span>

                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" aria-hidden />
                  resolve {describeMinutes(t.resolutionMinutes)}
                </span>

                {t.clock === "Continuous" && (
                  <span className="inline-flex items-center gap-1 text-xs text-warning">
                    <Zap className="h-3.5 w-3.5" aria-hidden />
                    24/7
                  </span>
                )}

                <TierChip tier={t.tier} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Where this figure came from.
 *
 * ⚠️ "Set here" versus "inherited" is the distinction the whole screen turns on. Without it an
 * administrator reads five figures on a category page and reasonably concludes all five were
 * chosen for that category.
 */
function TierChip({ tier }: { tier: SlaTier }) {
  if (tier === "Default") {
    return (
      <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
        inherited — organisation default
      </span>
    );
  }

  if (tier === "Team") {
    return (
      <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
        inherited — team target
      </span>
    );
  }

  return (
    <span className="ml-auto shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-secondary-foreground">
      set here
    </span>
  );
}

/**
 * Editing one priority.
 *
 * ⚠️ Prefilled from whatever is currently in force, inherited or not, because a row wins WHOLE
 * — saving writes first response, resolution and clock together, and there is no per-field
 * inheritance to fall back on. Starting from blank fields would make "I only wanted to change
 * the resolution time" into an invitation to invent a first-response figure.
 */
function TargetEditor({
  target,
  onCancel,
  onSave,
  onClear,
}: {
  target: SlaTarget;
  onCancel: () => void;
  onSave: (body: {
    firstResponseMinutes: number;
    resolutionMinutes: number;
    clock: SlaTarget["clock"];
  }) => Promise<void>;
  onClear: () => Promise<void>;
}) {
  const [firstResponse, setFirstResponse] = useState(String(target.firstResponseMinutes));
  const [resolution, setResolution] = useState(String(target.resolutionMinutes));
  const [clock, setClock] = useState<SlaTarget["clock"]>(target.clock);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsed = useMemo(
    () => ({ first: Number(firstResponse), resolve: Number(resolution) }),
    [firstResponse, resolution],
  );

  // Mirrors the server's own rule rather than replacing it — a zero or negative target would
  // make every new request breach on creation, and the domain refuses it either way.
  const valid =
    Number.isInteger(parsed.first) &&
    parsed.first > 0 &&
    Number.isInteger(parsed.resolve) &&
    parsed.resolve > 0;

  async function run(action: () => Promise<void>, fallback: string) {
    setBusy(true);
    setError(null);

    try {
      await action();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-ring bg-secondary/30 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">{target.priority}</span>
        <TierChip tier={target.tier} />
      </div>

      {error && (
        <div className="mt-2">
          <ErrorNote message={error} />
        </div>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <MinutesField
          id={`first-${target.priority}`}
          label="First reply within"
          value={firstResponse}
          onChange={setFirstResponse}
        />
        <MinutesField
          id={`resolve-${target.priority}`}
          label="Resolved within"
          value={resolution}
          onChange={setResolution}
        />
      </div>

      <fieldset className="mt-3">
        <legend className="text-xs font-medium">Clock</legend>
        <div className="mt-1 flex flex-wrap gap-2">
          {(
            [
              ["BusinessHours", "Business hours", "Counts only while the desk is open."],
              [
                "Continuous",
                "24/7",
                "Counts day and night. For work that cannot wait for morning.",
              ],
            ] as const
          ).map(([value, label, hint]) => (
            <label
              key={value}
              title={hint}
              className={cn(
                "flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition",
                clock === value
                  ? "border-ring bg-background"
                  : "border-border hover:border-ring/50",
              )}
            >
              <input
                type="radio"
                name={`clock-${target.priority}`}
                checked={clock === value}
                onChange={() => setClock(value)}
                className="h-4 w-4"
              />
              {label}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        {/* Offered only where something is actually set here — reverting an inherited value
            would be a button that does nothing and reports success. */}
        {target.tier !== "Default" && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(onClear, "That target could not be reverted.")}
            className="mr-auto inline-flex h-9 items-center gap-1.5 rounded-lg border border-input px-3 text-sm text-muted-foreground transition hover:border-ring hover:text-foreground disabled:opacity-50"
          >
            <RotateCcw className="h-4 w-4" aria-hidden />
            Revert to inherited
          </button>
        )}

        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="h-9 rounded-lg border border-input px-3 text-sm transition hover:border-ring disabled:opacity-50"
        >
          Cancel
        </button>

        <button
          type="button"
          disabled={busy || !valid}
          onClick={() =>
            void run(
              () =>
                onSave({
                  firstResponseMinutes: parsed.first,
                  resolutionMinutes: parsed.resolve,
                  clock,
                }),
              "That target could not be saved.",
            )
          }
          className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
        >
          <Save className="h-4 w-4" aria-hidden />
          {busy ? "Saving…" : "Save target"}
        </button>
      </div>
    </div>
  );
}

/**
 * Minutes, with the readable form beside it.
 *
 * Targets are stored in minutes because "one business day" is only a duration once opening
 * hours are known — but 3600 is not a number anybody can judge, so the readable form is shown
 * and the field still holds the minutes.
 */
function MinutesField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const minutes = Number(value);

  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="number"
          min={1}
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-28 rounded-lg border border-input bg-background px-3 text-base focus:border-ring focus:outline-none md:text-sm"
        />
        <span className="text-xs text-muted-foreground">
          minutes
          {Number.isInteger(minutes) && minutes > 0 && <> · {describeMinutes(minutes)}</>}
        </span>
      </div>
    </div>
  );
}
